/**
 * Hands-first VR controls. Mount inside the XROrigin (rig) so local
 * coordinates match the WebXR reference space.
 *
 *  - Wrist bar: look at the BACK of your left wrist (like checking a watch)
 *    and a small bar appears there — Menu in the middle, turn left / right on
 *    the sides, Turn around (180°) underneath. Press it with the other hand. Palm-toward-face is left to
 *    Meta's own system menu, so the two never overlap.
 *  - Teleport: pinch and hold while pointing at the floor (not at a button),
 *    an arc shows where you'll land; let go to move there. Landing on a seat
 *    sits you down (the rig's onTeleport handles seats).
 *
 * Controllers are untouched: everything here reads tracked hands only, and
 * stands down whenever a controller is in use.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { PointerCursorMaterial, PointerRayMaterial } from '@pmndrs/xr/internals';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { hands, handHead, handMode, listenPinch, palmFacingHead, updateHands, type HandSide } from './handTracking';
import { GestureDetector, GESTURE_EMOTE, gesturesEnabled } from './handGestures';
import { PINCH_GRACE_MS } from './pinch';
import { vrColors as colors, noRaycast } from '../vrUi';
import { useVRBuild } from '../build/vrBuildState';
import { useWorldStore } from '../../../stores/worldStore';
import VRWristBar, { wristBarPartAt } from './VRWristBar';
import { CHEST, isFurniture, jumpBlocked, onFurniture, pathBlocked } from '../vrReach';
import { insideBounds } from '../../../utils/worldBounds';

export const VR_MENU_EVENT = 'gathrxr:vr-menu';
/** Ask the active VR menu to toggle (wrist button) — the viewer and editor menus listen. */
export function toggleVRMenu() { window.dispatchEvent(new CustomEvent(VR_MENU_EVENT, { detail: { action: 'toggle' } })); }

const SNAP = Math.PI / 6; // 30°, same as the thumbstick
const HOLD_MS = 180;      // a quick pinch is a click; holding starts a teleport
const GRACE_MS = PINCH_GRACE_MS; // tracking can drop a pinch for a frame or two — don't end it (shared with the build tools)
const ARC_SPEED = 7.5;    // m/s launch speed of the aiming arc
const ARC_STEPS = 36;
const ARC_DT = 0.045;

/** Rotate the rig about the player's head so turning doesn't drift them. */
export function snapTurnRig(rig: THREE.Object3D, camera: THREE.Camera, rad: number) {
  const cam = new THREE.Vector3();
  camera.getWorldPosition(cam);
  const dx = cam.x - rig.position.x, dz = cam.z - rig.position.z;
  const c = Math.cos(rad), s = Math.sin(rad);
  rig.position.x += dx - (c * dx - s * dz);
  rig.position.z += dz - (s * dx + c * dz);
  rig.rotation.y += rad;
}

const TIPS_KEY = 'gathrxr.handTipsSeen.v2'; // v2: the wrist menu moved to the back of the wrist
function tipsSeen() { try { return localStorage.getItem(TIPS_KEY) === '1'; } catch { return false; } }

/** First-time card for hand tracking: the three gestures, then "Got it". */
function HandTips({ onDone }: { onDone: () => void }) {
  const [hover, setHover] = useState(false);
  const rows = [
    ['Pinch', 'Point at a button and pinch your thumb and finger to press it'],
    ['Pinch & hold', 'Point at the floor, hold, let go to move there. Gold ring on a seat = sit there'],
    ['Look at the back of your wrist', 'Like checking a watch: a bar appears with Menu, turn left / right and Turn around'],
    ['Gestures', 'Wave, clap twice, thumbs up, heart hands or both hands up — your avatar does it too'],
  ];
  return <group>
    <mesh raycast={noRaycast} position={[0, -.04, -.003]}><planeGeometry args={[.62, .48]} /><meshBasicMaterial color={colors.border} toneMapped={false} /></mesh>
    <mesh raycast={noRaycast} position={[0, -.04, -.002]}><planeGeometry args={[.612, .472]} /><meshBasicMaterial color={colors.panel} toneMapped={false} /></mesh>
    <Text raycast={noRaycast} position={[-.28, .16, 0]} anchorX="left" fontSize={.026} color={colors.text}>Your hands are your controllers</Text>
    {rows.map(([t, d], i) => <group key={t} position={[-.28, .085 - i * .085, 0]}>
      <Text raycast={noRaycast} anchorX="left" fontSize={.019} color={colors.teal}>{t}</Text>
      <Text raycast={noRaycast} position={[0, -.03, 0]} anchorX="left" fontSize={.0155} color={colors.muted} maxWidth={.56}>{d}</Text>
    </group>)}
    <group position={[0, -.235, .002]} name="vr-hands-tips-ok"
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); onDone(); }}>
      <mesh><planeGeometry args={[.16, .05]} /><meshBasicMaterial color={hover ? '#5ff0e3' : colors.teal} toneMapped={false} /></mesh>
      <Text raycast={noRaycast} position={[0, 0, .002]} fontSize={.02} color="#04211e">Got it</Text>
    </group>
  </group>;
}

/** Buttons and draggables (hover / press handlers) — pinching at these is a
 *  click, not a teleport. Objects that are only click-to-select (a venue, a
 *  placed model in the editor) don't count: you can walk on them. */
function isInteractive(o: THREE.Object3D | null) {
  for (let p = o; p; p = p.parent) {
    const h = (p as any).__r3f?.handlers;
    if (h && (h.onPointerOver || h.onPointerEnter || h.onPointerDown || h.onPointerMove)) return true;
  }
  return false;
}
function inside(o: THREE.Object3D, root: THREE.Object3D | null) {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p === root) return true;
  return false;
}
const COLLIDER_PROXY = /^(collider|ucx|collision)[_.]/i;
function isShown(o: THREE.Object3D) {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  return true;
}

export default function VRHandControls({ originRef, onTeleport, seatNear, onGesture, throughWalls = false }: {
  originRef: React.RefObject<THREE.Object3D | null>;
  /** Allow teleporting through walls (the editor, where builders work outside rooms too). */
  throughWalls?: boolean;
  /** `sit`: the arc ended on (or against) a seat — sit in it rather than stand there. */
  onTeleport: (point: THREE.Vector3, sit?: boolean) => void;
  /** Is there a seat to sit in here? `flat` = landing on a surface, not against its side. */
  seatNear?: (point: THREE.Vector3, flat: boolean) => boolean;
  /** Hand-gesture emotes (wave, clap, heart…): called with the emote to play. */
  onGesture?: (emote: string) => void;
}) {
  const session = useXR((s) => s.session);
  const { scene, camera } = useThree();
  const [wristSide, setWristSide] = useState<HandSide | null>(null);
  const [handsOn, setHandsOn] = useState(false);
  const wristRef = useRef<THREE.Group>(null);
  const detector = useMemo(() => new GestureDetector(), []);
  const [gestureLabel, setGestureLabel] = useState<string | null>(null);
  const gestureRef = useRef<THREE.Group>(null);
  const gestureTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (gestureTimer.current) clearTimeout(gestureTimer.current); }, []);
  const [showTips, setShowTips] = useState(false);
  const tipsRef = useRef<THREE.Group>(null);
  const tipsPlaced = useRef(false);
  const tipsAsked = useRef(false);

  // Pinch tracking with a short grace period: hand tracking can drop a held
  // pinch for a frame or two, which used to cancel the teleport. Intent
  // (click or teleport) is decided once the pinch has been held HOLD_MS.
  const pinch = useRef<Record<HandSide, { held: boolean; start: number; releasedAt: number; intent: 'none' | 'ui' | 'teleport' }>>({
    left: { held: false, start: 0, releasedAt: 0, intent: 'none' },
    right: { held: false, start: 0, releasedAt: 0, intent: 'none' },
  });
  const selectionAtPinch = useRef<string | null>(null);
  // Finger poke on the wrist bar (other hand's index tip).
  const [poke, setPoke] = useState<string | null>(null);
  const pokeDown = useRef(false);
  const lastPoke = useRef(0);
  const wristActions = useRef<Record<string, (() => void) | undefined>>({});
  useEffect(() => {
    if (!session) return;
    return listenPinch(session, (side, down) => {
      const p = pinch.current[side];
      const now = performance.now();
      if (down) {
        if (p.releasedAt && now - p.releasedAt < GRACE_MS) { p.releasedAt = 0; return; } // same pinch
        p.held = true; p.start = now; p.releasedAt = 0; p.intent = 'none';
      } else if (p.held) p.releasedAt = now;
    });
  }, [session]);

  // Candidate caches (refreshed every second): surfaces the arc can land on
  // (any visible solid mesh — floors in imported venues aren't always tagged —
  // plus hidden colliders), and buttons for the "is this a click?" test.
  const walkables = useRef<THREE.Object3D[]>([]);
  /** The world has a venue with its own collision floors (collider_* boxes). */
  const inVenue = useRef(false);
  const interactives = useRef<THREE.Object3D[]>([]);
  /** World boxes of furniture proxies (collider_furniture_*): no landing on them. */
  const furniture = useRef<THREE.Box3[]>([]);
  const lastCache = useRef(0);
  const refreshCaches = () => {
    walkables.current = [];
    interactives.current = [];
    furniture.current = [];
    const rig = originRef.current;
    let bvhBudget = 2; // build at most a couple of raycast trees per second
    let venue = false;
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || (m as any).isSkinnedMesh || (m as any).isTroikaText || (rig && inside(o, rig))) return;
      // The XR pointers' own ray / cursor visuals live in the scene too.
      if (m.material instanceof PointerCursorMaterial || m.material instanceof PointerRayMaterial) return;
      if (isInteractive(o)) { interactives.current.push(o); return; }
      // A venue's hidden collision boxes (collider_*) stand in for geometry
      // that is visible anyway — aim against what you can see, which is also
      // far fewer objects for the arc to test (a big venue has hundreds).
      if (COLLIDER_PROXY.test(o.name) && !isShown(o)) {
        venue = true;
        if (isFurniture(o)) furniture.current.push(new THREE.Box3().setFromObject(o));
        return;
      }
      const tagged = o.userData?.collidable || o.userData?.isMarbleCollider || o.name === 'ground' || o.name === 'vr-teleport-floor';
      const mat = m.material as THREE.Material | THREE.Material[];
      const seen = !Array.isArray(mat) && mat?.visible === false ? false : isShown(o);
      if (!tagged && !seen) return;
      walkables.current.push(o);
      const g = m.geometry as any;
      if (bvhBudget > 0 && g?.computeBoundsTree && !g.boundsTree && (g.index?.count ?? g.attributes?.position?.count ?? 0) > 3000) {
        try { g.computeBoundsTree(); bvhBudget--; } catch { /* non-indexable geometry */ }
      }
    });
    inVenue.current = venue;
  };

  // Arc visuals (rig-local).
  const arc = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array((ARC_STEPS + 1) * 3), 3));
    const mat = new THREE.LineBasicMaterial({ color: colors.teal, transparent: true, opacity: .9, depthTest: false });
    const line = new THREE.Line(geom, mat);
    line.frustumCulled = false; line.visible = false; line.renderOrder = 999;
    line.raycast = () => {};
    const ring = new THREE.Mesh(new THREE.RingGeometry(.16, .22, 32), new THREE.MeshBasicMaterial({ color: colors.teal, transparent: true, opacity: .85, depthTest: false, side: THREE.DoubleSide }));
    ring.rotation.x = -Math.PI / 2; ring.visible = false; ring.renderOrder = 999;
    ring.raycast = () => {};
    return { line, ring, geom, mat };
  }, []);
  useEffect(() => () => { arc.geom.dispose(); arc.mat.dispose(); (arc.ring.material as THREE.Material).dispose(); arc.ring.geometry.dispose(); }, [arc]);

  const ray = useMemo(() => { const r = new THREE.Raycaster(); (r as any).firstHitOnly = true; return r; }, []);
  const landing = useRef<{ point: THREE.Vector3; valid: boolean; sits: boolean } | null>(null);
  const tmp = useMemo(() => ({ o: new THREE.Vector3(), d: new THREE.Vector3(), p: new THREE.Vector3(), q: new THREE.Vector3(), v: new THREE.Vector3(), n: new THREE.Vector3(), m: new THREE.Matrix3(), local: new THREE.Vector3() }), []);

  useFrame((state, _delta, frame?: XRFrame) => {
    const origin = originRef.current;
    const ref = state.gl.xr.getReferenceSpace();
    if (!frame || !ref || !origin) return;
    updateHands(frame, ref);
    if (handsOn !== handMode.active) setHandsOn(handMode.active);
    if (handMode.active && !tipsAsked.current) { tipsAsked.current = true; if (!tipsSeen()) setShowTips(true); }
    // Park the tips card in front of the player once (it stays put, so it can be read and pressed).
    if (showTips && tipsRef.current && !tipsPlaced.current) {
      tmp.d.set(0, 0, -1).applyQuaternion(handHead.quat); tmp.d.y = 0;
      if (tmp.d.lengthSq() < 1e-4) tmp.d.set(0, 0, -1);
      tmp.d.normalize();
      tipsRef.current.position.copy(handHead.pos).addScaledVector(tmp.d, .75); tipsRef.current.position.y += .08;
      tipsRef.current.rotation.set(0, Math.atan2(-tmp.d.x, -tmp.d.z), 0);
      tipsPlaced.current = true;
    }
    if (!handMode.active) { arc.line.visible = arc.ring.visible = false; landing.current = null; return; }

    const now = performance.now();
    if (now - lastCache.current > 1000) { refreshCaches(); lastCache.current = now; }
    origin.updateMatrixWorld();

    // Pinch state with grace; a release only counts once the grace has passed.
    const P = pinch.current;
    const releasedNow: HandSide[] = [];
    for (const side of ['left', 'right'] as HandSide[]) {
      const p = P[side];
      if (!hands[side].present && p.held) p.releasedAt = p.releasedAt || now;
      if (p.held && p.releasedAt && now - p.releasedAt >= GRACE_MS) { p.held = false; releasedNow.push(side); }
    }
    const holding = (side: HandSide) => P[side].held;

    // ---- Gesture emotes (not while pinching / aiming / building) ----
    if (onGesture && !holding('left') && !holding('right') && gesturesEnabled()) {
      const g = detector.update(now, hands.left, hands.right, handHead.pos, handHead.quat);
      if (g) {
        onGesture(GESTURE_EMOTE[g].emote);
        setGestureLabel(GESTURE_EMOTE[g].label);
        if (gestureTimer.current) clearTimeout(gestureTimer.current);
        gestureTimer.current = setTimeout(() => setGestureLabel(null), 1600);
      }
    }
    if (gestureRef.current) {
      // Confirmation floats just in front of you, a little above eye line.
      tmp.d.set(0, 0, -1).applyQuaternion(handHead.quat); tmp.d.y = 0;
      if (tmp.d.lengthSq() < 1e-4) tmp.d.set(0, 0, -1);
      tmp.d.normalize();
      gestureRef.current.position.copy(handHead.pos).addScaledVector(tmp.d, .7); gestureRef.current.position.y += .18;
      gestureRef.current.rotation.set(0, Math.atan2(-tmp.d.x, -tmp.d.z), 0);
    }

    // ---- Wrist bar: on the back of the wrist you're looking at (left preferred) ----
    // Back of the hand toward the eyes = palmFacingHead strongly negative.
    // Never the palm side: that's where Meta's system menu / Meta buttons appear.
    // Left wrist only (the right hand presses it) — the right wrist stands in
    // only while the left hand isn't tracked.
    let want: HandSide | null = null;
    const barSide: HandSide = hands.left.present ? 'left' : 'right';
    {
      const h = hands[barSide];
      if (h.present && !holding(barSide) && -palmFacingHead(h) > (wristSide === barSide ? .45 : .7)) want = barSide;
    }
    if (want !== wristSide) setWristSide(want);
    let poked: string | null = null;
    if (want && wristRef.current) {
      const h = hands[want];
      // Just above the back of the wrist (palmNormal points out of the palm).
      wristRef.current.position.copy(h.wrist).lerp(h.palm, .25).addScaledVector(h.palmNormal, -.045);
      wristRef.current.lookAt(origin.localToWorld(tmp.local.copy(handHead.pos)));
      wristRef.current.updateMatrixWorld();
      // Poke: the other hand's index fingertip pressing into the bar's face
      // (+z points at your eyes). Fires once on contact; pull back to re-arm.
      const other = hands[want === 'left' ? 'right' : 'left'];
      if (other.present) {
        tmp.local.copy(other.indexTip); origin.localToWorld(tmp.local); wristRef.current.worldToLocal(tmp.local);
        const a = wristActions.current;
        const part = wristBarPartAt(tmp.local.x, tmp.local.y);
        const z = tmp.local.z;
        if (part && z < .06 && z > -.05) poked = part;
        if (part && z < .012 && z > -.05) {
          if (!pokeDown.current && now - lastPoke.current > 400) {
            pokeDown.current = true; lastPoke.current = now;
            a[part]?.();
          }
        } else if (z > .025 || !part) pokeDown.current = false;
      }
    }
    if (poked !== poke) setPoke(poked);

    // ---- Pinch: click or teleport (decided once the pinch is held) ----
    let aiming: HandSide | null = null;
    const both = holding('left') && holding('right');
    for (const side of ['right', 'left'] as HandSide[]) {
      const h = hands[side], p = P[side];
      if (!h.present || !p.held) continue;
      // Two hands pinching = a two-hand gesture, never a teleport.
      if (both) { p.intent = 'ui'; continue; }
      // Only a pinch that is still closed (not in its release grace) can become a teleport.
      if (p.intent === 'none' && !p.releasedAt && now - p.start > HOLD_MS) {
        // Placing or holding something in build mode owns the pinch, and so
        // does pointing at a button or panel control right now.
        const build = useVRBuild.getState();
        // The right hand holds palette items; a grab only ties up the hand doing it —
        // so the other hand can still teleport you while you carry something.
        const busy = !!build.placing || useWorldStore.getState().activeTool === 'place'
          || (!!build.held && side === 'right')
          || (build.handling && (build.handlingSide === null || build.handlingSide === side));
        tmp.o.copy(h.rayOrigin).applyMatrix4(origin.matrixWorld);
        tmp.d.copy(h.rayDir).transformDirection(origin.matrixWorld);
        ray.set(tmp.o, tmp.d); ray.far = 8;
        const ui = busy ? null : ray.intersectObjects(interactives.current.filter(isShown), false)[0];
        let onUi = !!ui;
        if (ui) {
          // A wall or floor in front of the button wins.
          ray.far = ui.distance;
          onUi = !ray.intersectObjects(walkables.current, false)[0];
        }
        p.intent = busy || onUi || want === side ? 'ui' : 'teleport';
        if (p.intent === 'teleport') selectionAtPinch.current = useWorldStore.getState().selectedObjectId;
      }
      if (p.intent === 'teleport') { aiming = side; break; }
    }

    if (aiming) {
      const h = hands[aiming];
      tmp.p.copy(h.rayOrigin).applyMatrix4(origin.matrixWorld);
      tmp.v.copy(h.rayDir).transformDirection(origin.matrixWorld).multiplyScalar(ARC_SPEED);
      const pos = arc.geom.getAttribute('position') as THREE.BufferAttribute;
      let hit: THREE.Intersection | null = null;
      let n = 0;
      tmp.local.copy(tmp.p); origin.worldToLocal(tmp.local); pos.setXYZ(n++, tmp.local.x, tmp.local.y, tmp.local.z);
      const targets = walkables.current;
      for (let i = 0; i < ARC_STEPS && !hit; i++) {
        tmp.q.copy(tmp.p).addScaledVector(tmp.v, ARC_DT);
        tmp.v.y -= 9.8 * ARC_DT;
        tmp.d.copy(tmp.q).sub(tmp.p);
        const len = tmp.d.length();
        ray.set(tmp.p, tmp.d.normalize()); ray.far = len;
        const h2 = ray.intersectObjects(targets, false)[0];
        if (h2) { hit = h2; tmp.q.copy(h2.point); }
        tmp.p.copy(tmp.q);
        tmp.local.copy(tmp.p); origin.worldToLocal(tmp.local); pos.setXYZ(n++, tmp.local.x, tmp.local.y, tmp.local.z);
      }
      for (let i = n; i <= ARC_STEPS; i++) pos.setXYZ(i, tmp.local.x, tmp.local.y, tmp.local.z);
      pos.needsUpdate = true;
      arc.geom.computeBoundingSphere();
      let valid = false, sits = false;
      if (hit) {
        tmp.n.copy(hit.face?.normal ?? tmp.n.set(0, 1, 0)).applyMatrix3(tmp.m.getNormalMatrix(hit.object.matrixWorld)).normalize();
        // Imported venues often have single-sided or flipped floor faces, so
        // judge flatness, not which way the face points.
        const flat = Math.abs(tmp.n.y) > .6;
        sits = !!seatNear?.(hit.point, flat);
        valid = flat || sits;
        // In a venue the invisible catch-all floor is never the real floor —
        // landing on it means the arc left the building (off a balcony).
        if (hit.object.name === 'vr-teleport-floor' && inVenue.current && !throughWalls) valid = false;
        // No landing on the far side of a wall (the arc can sail over one, or
        // through a gap in an imported room's mesh). Railings don't count.
        if (valid && !throughWalls) {
          tmp.o.copy(handHead.pos); origin.localToWorld(tmp.o); tmp.o.y -= .4;
          tmp.q.copy(hit.point); tmp.q.y += CHEST;
          // Hopping a railing only onto a real surface — never over a balcony
          // rail onto the invisible catch-all floor outside the venue.
          if (pathBlocked(targets, tmp.o, tmp.q) && (hit.object.name === 'vr-teleport-floor' || jumpBlocked(targets, tmp.o, tmp.q))) valid = false;
        }
        // Nor outside the world's walkable bounds (settings.bounds).
        if (valid && !throughWalls && !insideBounds(useWorldStore.getState().worldBounds, hit.point.x, hit.point.z)) valid = false;
        // Nor on a table or a pod — aiming at a seat (gold) still sits you in it.
        if (valid && !sits && !throughWalls && onFurniture(furniture.current, hit.point.x, hit.point.z)) valid = false;
        if (import.meta.env.DEV) (window as any).__gxLanding = { obj: (() => { const path: string[] = []; for (let o: THREE.Object3D | null = hit!.object; o && path.length < 6; o = o.parent) path.push(o.name || o.type); return path.join('<'); })(), n: tmp.n.toArray().map((x) => +x.toFixed(2)), p: hit.point.toArray().map((x) => +x.toFixed(2)) };
        landing.current = { point: hit.point.clone(), valid, sits };
        if (import.meta.env.DEV) Object.assign((window as any).__gxLanding, { valid, sits });
        arc.ring.position.copy(origin.worldToLocal(hit.point.clone())).y += .01;
        arc.ring.visible = true;
      } else {
        landing.current = null;
        arc.ring.visible = false;
      }
      // Teal: move here · gold: sit in this seat · red: can't land here.
      const color = !valid ? '#ff6b6b' : sits ? '#ffc861' : colors.teal;
      arc.mat.color.set(color);
      (arc.ring.material as THREE.MeshBasicMaterial).color.set(color);
      arc.line.visible = true;
    } else {
      arc.line.visible = arc.ring.visible = false;
    }

    // Released: teleport if the arc had a good landing spot.
    for (const rel of releasedNow) {
      const intent = P[rel].intent;
      P[rel].intent = 'none';
      if (intent === 'teleport' && landing.current?.valid) {
        onTeleport(landing.current.point.clone(), landing.current.sits);
        if (import.meta.env.DEV) (window as any).__gxTeleported = (((window as any).__gxTeleported) || 0) + 1;
        // In the editor the same pinch also "clicked" the floor/venue under
        // the arc (select / deselect) — moving shouldn't change the selection.
        const st = useWorldStore.getState();
        if (st.selectedObjectId !== selectionAtPinch.current) st.setSelectedObjectId(selectionAtPinch.current);
      }
      landing.current = null;
      arc.line.visible = arc.ring.visible = false;
    }
  });

  const turn = (rad: number) => { if (originRef.current) snapTurnRig(originRef.current, camera, rad); };

  // One wrist press per 400 ms, whether it came from a poke or a pinch-click.
  const pressWrist = (fn: () => void) => () => { const t = performance.now(); if (t - lastPoke.current < 400) return; lastPoke.current = t; fn(); };
  wristActions.current = { menu: toggleVRMenu, left: () => turn(SNAP), right: () => turn(-SNAP), around: () => turn(Math.PI) };

  const doneTips = () => { setShowTips(false); try { localStorage.setItem(TIPS_KEY, '1'); } catch { /* ignore */ } };

  return <>
    {handsOn && showTips && <group ref={tipsRef}><HandTips onDone={doneTips} /></group>}
    {gestureLabel && <group ref={gestureRef} name="vr-gesture-label">
      <mesh raycast={noRaycast} position={[0, 0, -.002]}><planeGeometry args={[.2, .05]} /><meshBasicMaterial color={colors.panel} transparent opacity={.85} toneMapped={false} /></mesh>
      <Text raycast={noRaycast} fontSize={.024} color={colors.teal}>{gestureLabel}</Text>
    </group>}
    <primitive object={arc.line} />
    <primitive object={arc.ring} />
    {/* Rays only: fingertip presses go through the poke above (not twice), and the
        wearing hand's own grab sphere sits right on the bar — it must not hover/press it. */}
    {handsOn && wristSide && <group ref={wristRef} pointerEventsType={{ allow: 'ray' }}>
      <VRWristBar onMenu={pressWrist(toggleVRMenu)} onTurnLeft={pressWrist(() => turn(SNAP))} onTurnRight={pressWrist(() => turn(-SNAP))}
        onTurnAround={pressWrist(() => turn(Math.PI))} poke={poke} />
    </group>}
  </>;
}
